import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { setClassNames } from 'ddc-classnames-js';
import {
	useSitemap,
	useLabels,
	useRequestData,
	usePrefs
} from 'wsm-common-data';
import PromoTypeLayer from './PromoTypeLayer';
import DetailsButton from '../DetailsButton';
import CustomAssetPromo from './CustomAssetPromo';
import { getDestinationLink, getClickHandler } from '../../utilities/links';
import {
	getDiscountDisplay,
	shouldCustomAssetDisclaimerBtnBeOnTop
} from '../../utilities/general';
import { getCouponStatus } from '../../utilities/aspect';
import { g } from '../../global-constants';
import { useURLPrefs } from '../../hooks/useURLPrefs';

const Promo = ({
	aspectRatio,
	carouselPosition,
	isMobile = false,
	isFixedProgramAspectRatio = false,
	isVisible = true,
	positionLabel = null,
	promo,
	role = null,
	roleDescription = null,
	width,
	onPromoPushOutsideButton,
	isCurrent = false,
	promosLength = 1
}) => {
	const {
		autoGenerated = false,
		condition = 'NEW',
		customAssets = {},
		destinationLabel = '',
		destinationUrl = '',
		discountType = '',
		discountValue = null,
		globalVehicleId = null,
		id,
		incentiveId = null,
		inventoryCount = 0,
		oemId = null,
		title = '',
		type
	} = promo;

	const labels = useLabels();
	const { enableCarousel, collection } = useURLPrefs();
	const sitemap = useSitemap();
	const { widgetName, windowId, locale } = useRequestData();
	const { reduceDollarSignSize } = usePrefs();
	const personalized = useSelector((state) => state.personalized);
	const isCoupon = getCouponStatus(aspectRatio);
	const isCouponMobile = isCoupon && isMobile;
	const isCustomAsset = customAssets && customAssets[aspectRatio];
	const disclaimerBtnOnTop =
		shouldCustomAssetDisclaimerBtnBeOnTop(aspectRatio);
	const classes = [
		'promo',
		`promo-type-${type.toLowerCase()}`,
		`collection-${collection.toLowerCase()}`,
		isCustomAsset ? 'custom-asset' : 'non-custom-asset',
		isCouponMobile && enableCarousel === 'false' ? 'mb-4' : ''
	];
	const leadFormURL = sitemap.get('PROMOTIONS_LEAD_FORM_FRAGMENT');

	// Handle destination link defaults
	const {
		url: ctaUrl,
		label: ctaLabel,
		isFullSlideLink
	} = getDestinationLink({
		condition,
		label: destinationLabel,
		url: destinationUrl,
		inventoryCount,
		isCustomAsset,
		labels,
		sitemap,
		type
	});
	// Create Discount display labels for FO promos
	const discountDisplay = getDiscountDisplay(
		locale,
		discountType,
		discountValue,
		labels,
		reduceDollarSignSize
	);

	let promoContent = <div className="hidden">Placeholder</div>;
	let addFullSlideCta = isFullSlideLink;
	const [customAssetDisclaimerBtn, setCustomAssetDisclaimerBtn] =
		useState(null);
	const incentives = useSelector((state) => state.incentives);
	const shouldShowIncentive =
		incentiveId &&
		globalVehicleId &&
		incentives &&
		incentives.length &&
		type === g.INCENTIVE;
	const [disableLeadDialog, setDisableLeadDialog] = useState(false);
	const promoIncentives = shouldShowIncentive
		? incentives.filter(
				(incentive) =>
					!incentive.error && incentiveId === incentive.incentiveId
		  )
		: [];

	if (isCustomAsset) {
		const incentive = promoIncentives.length ? promoIncentives[0] : null;
		// Disclaimer styles and classes
		const disclaimerOnAspectRatioBoxClasses = [
			'asset-disclaimer',
			'absolute-bottom',
			'absolute-left',
			'disclaimer-on-aspect-ratio-box'
		];
		const detailsButtonClasses = [
			disclaimerBtnOnTop ? 'text-black' : '',
			'my-1',
			'btn-no-decoration',
			'font-weight-normal'
		];

		const disclaimerBtn = (
			<div className={setClassNames(disclaimerOnAspectRatioBoxClasses)}>
				<DetailsButton
					btnStyle="link"
					carouselPosition={carouselPosition}
					customAsset
					classes={[setClassNames(detailsButtonClasses)]}
					isVisible={isVisible}
					listingConfigId=""
					promo={promo}
					incentive={incentive}
					fullLabel
					onShowLeadDialog={(el) => setDisableLeadDialog(el)}
				/>
			</div>
		);

		if (customAssetDisclaimerBtn === null)
			setCustomAssetDisclaimerBtn(disclaimerBtn);
	}

	useEffect(() => {
		if (isCurrent && !disclaimerBtnOnTop) {
			onPromoPushOutsideButton(customAssetDisclaimerBtn);
		}
		return () => {
			onPromoPushOutsideButton(null);
		};
	}, [isCurrent]);

	useEffect(() => {
		// Refreshing our ui-dialog module
		if (typeof window !== 'undefined' && isVisible) {
			if (isMobile) {
				window.DDC.mobile.slidein.init();
			} else {
				const refreshModuleEvent = new CustomEvent('modulesRefresh', {
					modules: {
						dialog: window?.DDC?.modules?.dialog
					}
				});
				document.dispatchEvent(refreshModuleEvent);
			}
		}
	}, [isMobile, isVisible]);

	if (!isVisible) {
		return null;
	}

	if (isCustomAsset) {
		// Render custom asset
		classes.push('slide-type-image');
		addFullSlideCta = !!ctaUrl;

		promoContent = (
			<CustomAssetPromo
				isMobile={isMobile}
				promosLength={promosLength}
				isVisible={isVisible}
				promo={promo}
				width={width}
				isFixedProgramAspectRatio={isFixedProgramAspectRatio}
			/>
		);
	} else {
		// Render HTML content
		classes.push('slide-type-html');

		promoContent = (
			<PromoTypeLayer
				carouselPosition={carouselPosition}
				ctaLabel={ctaLabel}
				ctaUrl={ctaUrl}
				discountDisplay={discountDisplay}
				isMobile={isMobile}
				isVisible={isVisible}
				promo={promo}
				incentive={promoIncentives.length ? promoIncentives[0] : null}
				width={width}
			/>
		);
	}

	if (promoContent) {
		const promoType = isCustomAsset ? 'Asset' : type;
		const clickHandler = getClickHandler(promoType, {
			widgetName,
			windowId,
			promo,
			aspectRatio,
			personalized,
			carouselPosition
		});
		let ctaAnchor;
		const isLeadCTA = ctaUrl === 'LEAD';

		/* eslint-disable jsx-a11y/anchor-is-valid */
		if (isLeadCTA) {
			ctaAnchor = (
				<a
					href="#"
					data-href={`${leadFormURL}?promotionId=${promo.id}`}
					tabIndex={!isVisible ? '-1' : null}
					onClick={clickHandler}
					rel="noreferrer"
					className={disableLeadDialog ? '' : 'dialog'}
					data-width="375"
					data-title={promo.destinationLabel}
				>
					{promoContent}
				</a>
			);
		} else {
			ctaAnchor = (
				<>
					<a
						href={
							ctaUrl === 'NO_DESTINATION_URL' ? undefined : ctaUrl
						}
						target={
							promo?.ctaConfig?.openInNewTab ? '_blank' : '_self'
						}
						tabIndex={!isVisible ? '-1' : null}
						onClick={clickHandler}
						rel="noreferrer"
					>
						{promoContent}
					</a>
				</>
			);
		}

		// Wrap with anchor if the full slide is a link
		// Set tab index when slide is not being displayed so that keyboard users can't access it
		const wrappedContent = addFullSlideCta ? ctaAnchor : promoContent;

		// TODO: need to figure out whether this promo was optimized with EO
		// data-promo-eo={true}
		// window.DDC.siteSettings.isEo <- Checks for the DNA resource

		return (
			<div
				className={setClassNames(classes)}
				data-promo-id={id}
				data-oem-id={oemId}
				data-title={!oemId ? title : null}
				// data-promo-eo={false}
				data-auto-generated={autoGenerated}
				data-url-href={addFullSlideCta ? ctaUrl : null}
				role={role}
				aria-current={isVisible ? 'true' : 'false'}
				aria-roledescription={roleDescription}
				aria-label={positionLabel}
			>
				{wrappedContent}
				{/* Render the disclaimer btn out of aspect ratio box
				for custom assets to not have 2 one unreachable anchor inside another */}
				{disclaimerBtnOnTop && customAssetDisclaimerBtn}
			</div>
		);
	}

	return null;
};

Promo.propTypes = {
	aspectRatio: PropTypes.string.isRequired,
	carouselPosition: PropTypes.number.isRequired,
	isMobile: PropTypes.bool,
	isVisible: PropTypes.bool,
	isFixedProgramAspectRatio: PropTypes.bool,
	isCurrent: PropTypes.bool,
	positionLabel: PropTypes.string,
	promo: PropTypes.shape({
		additionalDetails: PropTypes.string,
		autoGenerated: PropTypes.bool,
		condition: PropTypes.string,
		couponCode: PropTypes.string,
		customAssets: PropTypes.shape(),
		destinationLabel: PropTypes.string,
		destinationUrl: PropTypes.string,
		title: PropTypes.string,
		description: PropTypes.string,
		disclaimer: PropTypes.string,
		discountType: PropTypes.string,
		discountValue: PropTypes.number,
		globalVehicleId: PropTypes.string,
		id: PropTypes.string.isRequired,
		imageUrl: PropTypes.string,
		incentiveId: PropTypes.string,
		inventoryCount: PropTypes.number,
		type: PropTypes.string.isRequired,
		vehicleImages: PropTypes.arrayOf(PropTypes.string),
		oemId: PropTypes.string,
		ctaConfig: PropTypes.shape({
			openInNewTab: PropTypes.bool
		})
	}).isRequired,
	role: PropTypes.string,
	onPromoPushOutsideButton: PropTypes.func.isRequired,
	roleDescription: PropTypes.string,
	width: PropTypes.number.isRequired,
	promosLength: PropTypes.number
};

export default Promo;
